import React, { useState, useEffect, useRef } from "react";
import NotificationHistoryTable from "@/components/elements/schedulePush/NotificationHistoryTable";
import useHTTP from "../../hooks/useHTTP";
import { INotificationHistory } from "@/interfaces/ScheduledNotifications";
import ErrorMessage from "@/components/elements/global/ErrorMessage";
import useOnScreen from "@/components/hooks/useOnScreen";
import useDebounce from "@/components/hooks/useDebounce";
import { IconLibrary, ThriveIcon } from "thrive-components";

const ViewNotificationHistoryModal = ({ contentId, totalItems }) => {
  const { get, download } = useHTTP();
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [notificationDataLoading, setNotificationDataLoading] =
    useState<boolean>(true);
  const [contentTitle, setContentTitle] = useState<string | null>(null);
  const [notificationHistoryData, setNotificationHistoryData] =
    useState<INotificationHistory>({
      items: [
        {
          id: null,
          contentId: contentId,
          message: "",
          author: "",
          type: null,
          dateCreated: null,
          dateSent: null,
          status: null,
          audienceReach: null,
        },
      ],
      totalItems: totalItems,
      nextSkip: null,
      previousSkip: null,
    });

  //filter variables for requests
  const [skip, setSkip] = useState<number>(0);
  const [filterOptions, setFilterOptions] = useState<{
    sort: string;
    sortBy: string;
  }>({ sort: "Desc", sortBy: "sent" });

  function getQueryParams(): { [key: string]: any } {
    const queryParams = {};
    //formatting sort by params
    if (sort && sortBy) {
      if (sortBy === "Audience Reach") {
        queryParams["sortBy"] = "Reach";
      } else {
        queryParams["sortBy"] = sortBy;
      }
      //formatting sorttype params
      if (sort === "asc") {
        queryParams["sortType"] = "Asc";
      } else {
        queryParams["sortType"] = "Desc";
      }
    }
    return queryParams;
  }

  //setting and sanitising the content title for csv filename
  //initial content load
  useEffect(() => {
    let title = getContentTitle()
    if (title) {
      title = title.replaceAll(" ", "")
    }
    setContentTitle(title);
  }, []);

  //on each change of the filter, we update the data
  const { sortBy, sort } = filterOptions;
  useEffect(() => {
    getFilteredNotificationHistory("GET_FILTER");
  }, [sortBy, sort]);

  function getFilteredNotificationHistory(type: "GET_MORE" | "GET_FILTER") {
    get(`notifications/content/${contentId}/notificationhistory`, {
      params: {
        ...getQueryParams(),
        skip: type === "GET_FILTER" ? 0 : skip,
        top: 20,
      },
    })
      .then(({ data }) => {
        setNotificationDataLoading(true);
        if (type === "GET_MORE") {
          setNotificationHistoryData((prevData) => ({
            ...prevData,
            items: [...prevData.items, ...data.items],
            totalItems: data.totalItems,
            nextSkip: data.nextSkip,
            previousSkip: data.previousSkip,
          }));
        } else {
          setNotificationHistoryData(data);
        }
        setSkip(data.nextSkip);
      })
      .catch((err) => {
        console.log(err);
        setShowErrorMessage(true);
      })
      .finally(() => {
        setNotificationDataLoading(false);
      });
  }

  //sources the content title for the download file name, default to untitled where none found
  function getContentTitle() {
    if (document.querySelector("#Title") as HTMLInputElement) {
      return (document.querySelector("#Title") as HTMLInputElement).value;
    }
    return "untitled";
  }

  //downloads csv data to device
  const downloadHistoryClick = () => {
    download(
      `notifications/content/${contentId}/notificationhistoryreport`,
      `${contentTitle}NotificationHistory.csv`
    );
  };

  //Pagenation Logic:
  // Ref for infinite scrolling
  const scrollTo = useRef();
  const isVisible = useOnScreen(
    scrollTo,
    document.querySelector(".table-wrapper")
  );

  //delays the isVisible by 3 seconds
  let isVisibleDebounced = useDebounce(isVisible, 2000);

  // If there is data and total items are the same as skip value, get more notifications for the table
  useEffect(() => {
    if (
      isVisible &&
      isVisibleDebounced &&
      totalItems > skip &&
      notificationHistoryData?.items.length > 19
    ) {
      getFilteredNotificationHistory("GET_MORE");
    }
  }, [isVisible, isVisibleDebounced]);

  if (notificationDataLoading) {
    return <></>;
  }

  return (
    <>
      <div className="notification-history-container">
        <div
          className="notification-history-title"
        >
          Notification History
        </div>
        {!showErrorMessage && (
          <button
            className="download-button"
            aria-label="Download Notification History File"
            onClick={downloadHistoryClick}
          >
            <ThriveIcon 
              color="var(--thriveCharcoal)"
              sizeInPx="12"
              iconName={IconLibrary.DOWNLOAD}
            />
          </button>
        )}
      </div>
      {showErrorMessage ? (
        <ErrorMessage
          className={"error-loading-history-table"}
          errorMessage={
            "Something went wrong. Please refresh the page and try again."
          }
        />
      ) : (
        <section id="notification-history-table-details">
          <NotificationHistoryTable
            notificationHistory={notificationHistoryData}
            filterOptions={filterOptions}
            setFilterOptions={setFilterOptions}
            scrollTo={scrollTo}
            isLoading={notificationDataLoading}
          />
        </section>
      )}
    </>
  );
};

export default ViewNotificationHistoryModal;
